'use client'

import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Code2 } from 'lucide-react'

export default function TechnologyStack() {
  const [activeTab, setActiveTab] = useState('frontend')

  const categories = [
    { id: 'frontend', label: 'Frontend', color: 'from-sky-400 to-blue-500' },
    { id: 'backend', label: 'Backend', color: 'from-emerald-400 to-teal-500' },
    { id: 'cloud', label: 'Cloud', color: 'from-orange-400 to-red-500' },
    { id: 'database', label: 'Database', color: 'from-violet-400 to-purple-500' },
  ]

  const technologies: Record<string, { name: string; icon: string }[]> = {
    frontend: [
      { name: 'React.js', icon: '⚛️' }, { name: 'Next.js', icon: '▲' },
      { name: 'Vue.js', icon: '🟢' }, { name: 'Angular', icon: '🅰️' },
      { name: 'React Native', icon: '📱' }, { name: 'Flutter', icon: '💙' },
    ],
    backend: [
      { name: 'Node.js', icon: '🟢' }, { name: 'Python', icon: '🐍' },
      { name: 'Java', icon: '☕' }, { name: '.NET', icon: '🔷' },
      { name: 'PHP', icon: '🐘' }, { name: 'Ruby', icon: '💎' },
    ],
    cloud: [
      { name: 'AWS', icon: '☁️' }, { name: 'Azure', icon: '🔵' },
      { name: 'GCP', icon: '🟡' }, { name: 'Docker', icon: '🐳' },
      { name: 'Kubernetes', icon: '⚙️' }, { name: 'Firebase', icon: '🔥' },
    ],
    database: [
      { name: 'MongoDB', icon: '🍃' }, { name: 'PostgreSQL', icon: '🐘' },
      { name: 'MySQL', icon: '🐬' }, { name: 'Redis', icon: '🔴' },
      { name: 'ElasticSearch', icon: '🔍' }, { name: 'DynamoDB', icon: '⚡' },
    ],
  }

  return (
    <section className="py-20 sm:py-28 bg-white relative overflow-hidden">
      <div className="absolute top-1/2 left-0 -translate-y-1/2 w-72 h-72 bg-sky-50 rounded-full blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-sky-100 text-sky-600 text-sm font-medium mb-4"
          >
            <Code2 className="w-4 h-4" />
            Tech Stack
          </motion.div>
          <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            Our <span className="gradient-text">Technology</span>
          </h2>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto">
            Production-focused stack used across mobile, web, cloud, AI and IoT
          </p>
        </motion.div>

        {/* Tabs */}
        <div className="flex justify-center mb-8 sm:mb-10">
          <div className="inline-flex bg-sky-50 rounded-xl p-1 sm:p-1.5 gap-0.5 sm:gap-1 flex-wrap justify-center">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveTab(cat.id)}
                className={`relative px-4 sm:px-5 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-all duration-300 ${
                  activeTab === cat.id
                    ? 'text-white shadow-md'
                    : 'text-slate-600 hover:text-sky-600'
                }`}
              >
                {activeTab === cat.id && (
                  <motion.div
                    layoutId="activeTechTab"
                    className={`absolute inset-0 bg-gradient-to-r ${cat.color} rounded-lg`}
                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                  />
                )}
                <span className="relative z-10">{cat.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tech Grid */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.3 }}
            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4 max-w-4xl mx-auto"
          >
            {technologies[activeTab].map((tech, index) => (
              <motion.div
                key={tech.name}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ y: -4, scale: 1.05 }}
                className="bg-white rounded-2xl p-5 shadow-sm border border-sky-100/50 hover:shadow-lg hover:shadow-sky-100/50 hover:border-sky-200 transition-all duration-300 text-center group"
              >
                <div className="text-3xl mb-2 group-hover:scale-110 transition-transform duration-300">{tech.icon}</div>
                <p className="text-xs font-medium text-slate-700 group-hover:text-sky-600 transition-colors">{tech.name}</p>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  )
}
